import { useState, useEffect, useCallback } from "react";
import { Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography, TextField, Autocomplete, ToggleButtonGroup, ToggleButton } from "@mui/material";
import { CheckCircle, Download } from '@mui/icons-material';
import apiService from "../../service/apiService";
import { FaEye, FaRegEdit, FaTrash } from "react-icons/fa";
import { useNavigate } from 'react-router-dom';
import '../../assets/styles/cropmanage.css';
import * as XLSX from "xlsx";
import { Pagination } from "react-bootstrap";
import { debounce } from "lodash";

const Tlseed = () => {
  const navigate = useNavigate();
  
  // Essential state variables only
  const [pageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedRow, setSelectedRow] = useState({ receivingQuantity: "" });
  const [farmers, setFarmers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [seasons, setSeasons] = useState([]);
  const [castes, setCastes] = useState([]);
  const [totalItems, setTotalitems] = useState(0);
  const [totalpage, setTotalpage] = useState(0);
  const [years, setYears] = useState([]);
  const [unit, setUnit] = useState(2);
  
  // Filter states
  const [producingFarmer, setproducingFarmer] = useState('');
  const [pgCode, setPgCode] = useState('');
  const [caste, setCaste] = useState("");
  const [seasonName, setSeasonName] = useState('');
  const [seedName, setSeedName] = useState('');
  const [variety, setVariety] = useState("");
  const [totalTLSeed, setTotalTLSeed] = useState(null);
  const [receivingFarmer, setReceivingFarmer] = useState('');
  const [receivingQuantity, setReceivingQuantity] = useState(null);
  const [address, setAddress] = useState('');
  const [areaCoverage, setAreaCoverage] = useState(null);
  const [remainingTLSeed, setRemainingTLSeed] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [year, setYear] = useState(null);

  const loginData = JSON.parse(localStorage.getItem("loginData"));
  const [dist, setDist] = useState(loginData?.districtId ? loginData.districtId : 0);
  const [blk, setBlk] = useState(loginData?.blockId ? loginData.blockId : 0);

  const fetchData = async () => {
    try {
      const payload = {
        producingFarmerId: 0,
        receivingFarmerId: 0,
        producingFarmerName: producingFarmer || "",
        receivingFarmerName: receivingFarmer || "",
        villageName: "",
        pgCode: pgCode || "",
        pgName: "",
        seasonName: seasonName || "",
        year: year ? parseInt(year, 10) : null,
        seasonId: 0,
        pageNumber: page,
        pageSize: pageSize,
        seedName: seedName || "",
        address: address || "",
        areaCoverage: areaCoverage || null,
        caste: caste || "",
        remainingTLSeed: remainingTLSeed || null,
        totalTLSeed: totalTLSeed || null,
        receivingQuantity: receivingQuantity || null,
        varietyName: variety || "",
        remarks: remarks || "",
        districtId: dist,
        blockId: blk
      };

      const response = await apiService.post("/tl-outreach/get-index-data", payload);
      const { data, recordsCount, pageCount } = response.data.body;

      const formattedData = data.map((item) => ({
        id: item.tlOutReachId,
        producingFarmer: item.producingFarmer || "N/A",
        receivingFarmer: item.receivingFarmer || "N/A",
        villageName: item.villageName || "N/A",
        pgCode: item.pgCode,
        pgName: item.pgName,
        seasonName: item.seasonName,
        year: item.year,
        seasonId: item.seasonId,
        seedName: item.seedName || "N/A",
        address: item.address,
        areaCoverage: item.areaCoverage,
        caste: item.caste,
        remainingTLSeed: item.remainingTLSeed,
        totalTLSeed: item.totalTLSeed,
        receivingQuantity: item.receivingQuantity,
        varietyName: item.varietyName || "N/A",
        createdAt: new Date(item.createdAt).toLocaleDateString(),
        remarks: item.remarks,
        receivingFarmerId: item.receivingFarmerId
      }));

      setRows(formattedData);
      setTotalitems(recordsCount);
      setTotalpage(pageCount);
    } catch (error) {
      console.error("Error fetching data:", error);
      setRows([]);
      setTotalitems(0);
      setTotalpage(0);
    }
  };

  const debouncedFetchData = useCallback(debounce(fetchData, 500), [
    producingFarmer, seasonName, address, areaCoverage, seedName, variety, caste, 
    pgCode, receivingFarmer, remainingTLSeed, totalTLSeed, receivingQuantity, 
    remarks, year, page, pageSize, blk, dist
  ]);

  useEffect(() => {
    debouncedFetchData();
    return () => debouncedFetchData.cancel();
  }, [debouncedFetchData]);

  const fetchYears = async () => {
    try {
      const response = await apiService.get("/years/get-all");
      setYears(response.data);
      const currentYear = new Date().getFullYear();
      const defaultYear = response.data.find((yr) => yr.yearValue === currentYear);
      setYear(defaultYear ? defaultYear.yearValue : response.data[0].yearValue);
    } catch (error) {
      console.error("Error fetching years:", error);
    }
  };

  const fetchCastes = async () => {
    try {
      const response = await apiService.get("/castes/get-all");
      setCastes(response.data);
    } catch (error) {
      console.error("Error fetching caste data:", error);
    }
  };

  const fetchSeasons = async () => {
    try {
      const response = await apiService.get("/seasons/get-all");
      setSeasons(response.data);
    } catch (error) {
      console.error("Error fetching seasons data:", error);
    }
  };

  const fetchFarmers = async () => {
    setLoading(true);
    try {
      const response = await apiService.get("/farmers/get-all");
      setFarmers(response.data);
    } catch (error) {
      console.error("Error fetching farmers:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchYears();
    fetchCastes();
    fetchSeasons();
    fetchFarmers();
  }, []);

  const handleEdit = (id) => {
    const rowToEdit = rows.find((row) => row.id === id);
    setSelectedRow(rowToEdit);
    setOpenEdit(true);
  };

  const handleDelete = (id) => {
    setSelectedRow(rows.find((row) => row.id === id));
    setOpenDelete(true);
  };

  const handleDeleteRow = async () => {
    try {
      const response = await apiService.delete(`/tl-outreach/delete/${selectedRow.id}`);
      if (response.status === 200) {
        setRows(rows.filter(row => row.id !== selectedRow.id));
        setOpenDelete(false);
        setSelectedRow(null);
        alert("Item successfully deleted.");
      } else {
        alert("Failed to delete the item.");
      }
    } catch (error) {
      alert("Error deleting the item.");
      console.error("Error deleting the item:", error);
    } finally {
      setOpenDelete(false);
    }
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setSelectedRow((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUnitChange = (event, newUnit) => {
    if (newUnit !== null) {
      setUnit(newUnit);
    }
  };

  const handleSaveChanges = async () => {
    try {
      if (!selectedRow || !selectedRow.id) {
        console.error("No selected row to update.");
        return;
      }

      const url = `/tl-outreach/update/${selectedRow.id}`;
      const payload = selectedRow.receivingFarmer && selectedRow.address
        ? {
          receivingQuantity: parseFloat(selectedRow.receivingQuantity) || 0,
          remarks: selectedRow.remarks,
          receivingFarmer: selectedRow.receivingFarmer,
          address: selectedRow.address,
          areaCoverage: selectedRow.areaCoverage,
          unit: unit,
        }
        : {
          receivingFarmerId: selectedRow.receivingFarmerId,
          receivingQuantity: parseFloat(selectedRow.receivingQuantity) || 0,
          remarks: selectedRow.remarks,
          areaCoverage: selectedRow.areaCoverage,
          unit: unit,
        };

      const response = await apiService.put(url, payload);

      if (response.status === 200) {
        setRows(rows.map((row) => (row.id === selectedRow.id ? selectedRow : row)));
        console.log("Row updated successfully:", response.data);
        setOpenEdit(false);
        fetchData();
      } else {
        console.error("Failed to update row:", response);
      }
    } catch (error) {
      console.error("Error while updating row:", error);
    }
  };

  const handlePageChange = (pageNumber) => {
    setPage(pageNumber);
  };

  const renderPaginationItems = () => {
    const paginationItems = [];
    const maxPagesToShow = 7;
    const startPages = 3;
    const endPages = 2;

    if (totalpage <= maxPagesToShow) {
      for (let pages = 1; pages <= totalpage; pages++) {
        paginationItems.push(
          <Pagination.Item key={pages} active={page === pages} onClick={() => handlePageChange(pages)}>
            {pages}
          </Pagination.Item>
        );
      }
    } else {
      for (let pages = 1; pages <= startPages; pages++) {
        paginationItems.push(
          <Pagination.Item key={pages} active={page === pages} onClick={() => handlePageChange(pages)}>
            {pages}
          </Pagination.Item>
        );
      }

      if (page > startPages + 1) {
        paginationItems.push(<Pagination.Ellipsis key="start-ellipsis" />);
      }

      const middlePages = [];
      const startMiddlePage = Math.max(page - 1, startPages + 1);
      const endMiddlePage = Math.min(page + 1, totalpage - endPages);

      for (let pages = startMiddlePage; pages <= endMiddlePage; pages++) {
        middlePages.push(
          <Pagination.Item key={pages} active={page === pages} onClick={() => handlePageChange(pages)}>
            {pages}
          </Pagination.Item>
        );
      }

      paginationItems.push(...middlePages);

      if (page < totalpage - endPages - 1) {
        paginationItems.push(<Pagination.Ellipsis key="end-ellipsis" />);
      }

      for (let pages = totalpage - endPages + 1; pages <= totalpage; pages++) {
        paginationItems.push(
          <Pagination.Item key={pages} active={page === pages} onClick={() => handlePageChange(pages)}>
            {pages}
          </Pagination.Item>
        );
      }
    }

    return paginationItems;
  };

  const exportToExcel = async () => {
    try {
      const payload = {
        producingFarmerId: 0,
        receivingFarmerId: 0,
        producingFarmerName: "",
        receivingFarmerName: "",
        villageName: "",
        pgCode: "",
        pgName: "",
        seasonName: "",
        year: year,
        seasonId: 0,
        pageNumber: 1,
        pageSize: 1000000000,
        seedName: "",
        address: "",
        areaCoverage: null,
        caste: "",
        remainingTLSeed: null,
        totalTLSeed: null,
        receivingQuantity: null,
        varietyName: "",
        remarks: "",
        districtId: dist,
        blockId: blk
      };

      const response = await apiService.post("/tl-outreach/get-index-data", payload);
      const { data } = response.data.body;

      const headers = [
        ['Producer Farmer Name', 'PG Code', 'Caste', 'Season', 'Crop', 'Variety', 
         'Total TL Seed (kg)', 'Receiving Quantity (kg)', 'Receiving Farmer', 
         'Address', 'Area Coverage (ha)', 'Remaining TL Seed (kg)', 'Remarks']
      ];

      const dataRows = data.map((item) => [
        item.producingFarmer || "N/A",
        item.pgCode || "N/A",
        item.caste || "N/A",
        item.seasonName || "N/A",
        item.seedName || "N/A",
        item.varietyName || "N/A",
        item.totalTLSeed || "N/A",
        item.receivingQuantity || "N/A",
        item.receivingFarmer || "N/A",
        item.address || "N/A",
        item.areaCoverage || "N/A",
        item.remainingTLSeed || "N/A",
        item.remarks || "N/A",
      ]);

      const worksheetData = [...headers, ...dataRows];
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "TL Outreach Data");
      XLSX.writeFile(workbook, `TL_Outreach_Data_${year}.xlsx`);
    } catch (error) {
      console.error("Error exporting data:", error);
    }
  };

  const handleAddTlOutreach = () => {
    navigate('/addtloutreach');
  };

  const startItem = (page - 1) * 10 + 1;
  const endItem = Math.min(page * 10, totalItems);

  return (
    <Box m="2px">
      <div style={{ maxHeight: "100vh", overflowY: "auto" }}>
        {/* Header - Same design as marketlinkage */}
        <div style={{
          backgroundColor: "#ffffff",
          borderBottom: "3px solid #007bff",
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)"
        }}>
          <div className="container-fluid">
            <div className="d-flex justify-content-between align-items-center py-3 px-4">
              <div className="d-flex align-items-center">
                <div>
                  <h1 style={{
                    fontSize: "1.8rem",
                    fontWeight: "600",
                    color: "#2c3e50",
                    margin: "0"
                  }}>
                    TL-Outreach
                  </h1>
                </div>
              </div>

              <div className="d-flex align-items-center gap-3">
                <div className="d-flex align-items-center">
                  <label style={{
                    fontWeight: "500",
                    color: "#495057",
                    marginRight: "10px",
                    fontSize: "14px"
                  }}>
                    Year:
                  </label>
                  <select
                    className="form-select"
                    value={year || 0}
                    onChange={(e) => setYear(Number(e.target.value))}
                    style={{
                      border: "1px solid #ced4da",
                      borderRadius: "6px",
                      padding: "6px 12px",
                      fontSize: "14px",
                      minWidth: "100px",
                      backgroundColor: "#fff"
                    }}
                  >
                    <option value={0} disabled>Select Year</option>
                    {years.map((yr) => (
                      <option key={yr.yearId} value={yr.yearValue}>
                        {yr.yearValue}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="d-flex align-items-center gap-3">
                  <Button
                    variant="contained"
                    onClick={handleAddTlOutreach}
                    style={{
                      backgroundColor: "#28a745",
                      border: "none",
                      borderRadius: "6px",
                      padding: "8px 16px",
                      fontSize: "14px",
                      fontWeight: "500",
                      textTransform: "none"
                    }}
                  >
                    Add TL-Outreach
                  </Button>
                  
                  <Button
                    variant="contained"
                    onClick={exportToExcel}
                    style={{
                      backgroundColor: "#007bff",
                      border: "none",
                      borderRadius: "6px",
                      padding: "8px 16px",
                      fontSize: "14px",
                      fontWeight: "500",
                      textTransform: "none"
                    }}
                  >
                    Export Excel
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Table Section */}
        <div className="mx-2" style={{ minHeight: "75vh", overflowY: "auto", overflowX: "auto", maxWidth: "100%", whiteSpace: "nowrap" }}>
          <table className="table table-hover mb-0" style={{
            minWidth: "1800px",
            backgroundColor: "#fff"
          }}>
            <thead style={{
              backgroundColor: "#007bff",
              color: "white",
              position: "sticky",
              top: "0",
              zIndex: "10"
            }}>
              <tr>
                <th style={{
                  padding: "15px 12px",
                  fontWeight: "600",
                  fontSize: "13px",
                  borderRight: "1px solid rgba(255,255,255,0.2)",
                  minWidth: "150px"
                }}>
                  Producer Farmer (Name)
                  <br />
                  <input
                    type="text"
                    placeholder="Search"
                    className="form-control form-control-sm mt-2"
                    value={producingFarmer}
                    onChange={(e) => { setproducingFarmer(e.target.value); setPage(1); }}
                    style={{
                      backgroundColor: "#ffffff",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      fontSize: "12px",
                      color: "#333",
                      padding: "4px 8px"
                    }}
                  />
                </th>
                <th style={{
                  padding: "15px 12px",
                  fontWeight: "600",
                  fontSize: "13px",
                  borderRight: "1px solid rgba(255,255,255,0.2)",
                  minWidth: "120px"
                }}>
                  PG Code
                  <br />
                  <input
                    type="text"
                    placeholder="Search"
                    className="form-control form-control-sm mt-2"
                    value={pgCode}
                    onChange={(e) => { setPgCode(e.target.value); setPage(1); }}
                    style={{
                      backgroundColor: "#ffffff",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      fontSize: "12px",
                      color: "#333",
                      padding: "4px 8px"
                    }}
                  />
                </th>
                <th style={{
                  padding: "15px 12px",
                  fontWeight: "600",
                  fontSize: "13px",
                  borderRight: "1px solid rgba(255,255,255,0.2)",
                  minWidth: "100px"
                }}>
                  Caste
                  <br />
                  <select
                    value={caste}
                    onChange={(e) => { setCaste(e.target.value); setPage(1); }}
                    className="form-control form-control-sm mt-2"
                    style={{
                      backgroundColor: "#ffffff",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      fontSize: "12px",
                      color: "#333",
                      padding: "4px 8px"
                    }}
                  >
                    <option value="">All</option>
                    {castes.map((casteOption) => (
                      <option key={casteOption.casteId} value={casteOption.caste}>
                        {casteOption.caste}
                      </option>
                    ))}
                  </select>
                </th>
                <th style={{
                  padding: "15px 12px",
                  fontWeight: "600",
                  fontSize: "13px",
                  borderRight: "1px solid rgba(255,255,255,0.2)",
                  minWidth: "100px"
                }}>
                  Season
                  <br />
                  <select
                    value={seasonName}
                    onChange={(e) => { setSeasonName(e.target.value); setPage(1); }}
                    className="form-control form-control-sm mt-2"
                    style={{
                      backgroundColor: "#ffffff",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      fontSize: "12px",
                      color: "#333",
                      padding: "4px 8px"
                    }}
                  >
                    <option value="">All</option>
                    {seasons.map((season) => (
                      <option key={season.seasonId} value={season.seasonName}>
                        {season.seasonName}
                      </option>
                    ))}
                  </select>
                </th>
                <th style={{
                  padding: "15px 12px",
                  fontWeight: "600",
                  fontSize: "13px",
                  borderRight: "1px solid rgba(255,255,255,0.2)",
                  minWidth: "100px"
                }}>
                  Crop
                  <br />
                  <input
                    type="text"
                    placeholder="Search"
                    className="form-control form-control-sm mt-2"
                    value={seedName}
                    onChange={(e) => { setSeedName(e.target.value); setPage(1); }}
                    style={{
                      backgroundColor: "#ffffff",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      fontSize: "12px",
                      color: "#333",
                      padding: "4px 8px"
                    }}
                  />
                </th>
                <th style={{
                  padding: "15px 12px",
                  fontWeight: "600",
                  fontSize: "13px",
                  borderRight: "1px solid rgba(255,255,255,0.2)",
                  minWidth: "100px"
                }}>
                  Variety
                  <br />
                  <input
                    type="text"
                    placeholder="Search"
                    className="form-control form-control-sm mt-2"
                    value={variety}
                    onChange={(e) => { setVariety(e.target.value); setPage(1); }}
                    style={{
                      backgroundColor: "#ffffff",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      fontSize: "12px",
                      color: "#333",
                      padding: "4px 8px"
                    }}
                  />
                </th>
                <th style={{
                  padding: "15px 12px",
                  fontWeight: "600",
                  fontSize: "13px",
                  borderRight: "1px solid rgba(255,255,255,0.2)",
                  minWidth: "120px"
                }}>
                  TL Seed (Kg)
                  <br />
                  <input
                    type="text"
                    placeholder="Search"
                    className="form-control form-control-sm mt-2"
                    value={totalTLSeed}
                    onChange={(e) => { setTotalTLSeed(e.target.value); setPage(1); }}
                    style={{
                      backgroundColor: "#ffffff",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      fontSize: "12px",
                      color: "#333",
                      padding: "4px 8px"
                    }}
                  />
                </th>
                <th style={{
                  padding: "15px 12px",
                  fontWeight: "600",
                  fontSize: "13px",
                  borderRight: "1px solid rgba(255,255,255,0.2)",
                  minWidth: "150px"
                }}>
                  Receiving Farmer(Name)
                  <br />
                  <input
                    type="text"
                    placeholder="Search"
                    className="form-control form-control-sm mt-2"
                    value={receivingFarmer}
                    onChange={(e) => { setReceivingFarmer(e.target.value); setPage(1); }}
                    style={{
                      backgroundColor: "#ffffff",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      fontSize: "12px",
                      color: "#333",
                      padding: "4px 8px"
                    }}
                  />
                </th>
                <th style={{
                  padding: "15px 12px",
                  fontWeight: "600",
                  fontSize: "13px",
                  borderRight: "1px solid rgba(255,255,255,0.2)",
                  minWidth: "140px"
                }}>
                  Received Quantity (kg)
                  <br />
                  <input
                    type="text"
                    placeholder="Search"
                    className="form-control form-control-sm mt-2"
                    value={receivingQuantity}
                    onChange={(e) => { setReceivingQuantity(e.target.value); setPage(1); }}
                    style={{
                      backgroundColor: "#ffffff",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      fontSize: "12px",
                      color: "#333",
                      padding: "4px 8px"
                    }}
                  />
                </th>
                <th style={{
                  padding: "15px 12px",
                  fontWeight: "600",
                  fontSize: "13px",
                  borderRight: "1px solid rgba(255,255,255,0.2)",
                  minWidth: "120px"
                }}>
                  Address
                  <br />
                  <input
                    type="text"
                    placeholder="Search"
                    className="form-control form-control-sm mt-2"
                    value={address}
                    onChange={(e) => { setAddress(e.target.value); setPage(1); }}
                    style={{
                      backgroundColor: "#ffffff",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      fontSize: "12px",
                      color: "#333",
                      padding: "4px 8px"
                    }}
                  />
                </th>
                <th style={{
                  padding: "15px 12px",
                  fontWeight: "600",
                  fontSize: "13px",
                  borderRight: "1px solid rgba(255,255,255,0.2)",
                  minWidth: "120px"
                }}>
                  Area Covered(ha)
                  <br />
                  <input
                    type="text"
                    placeholder="Search"
                    className="form-control form-control-sm mt-2"
                    value={areaCoverage}
                    onChange={(e) => { setAreaCoverage(e.target.value); setPage(1); }}
                    style={{
                      backgroundColor: "#ffffff",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      fontSize: "12px",
                      color: "#333",
                      padding: "4px 8px"
                    }}
                  />
                </th>
                <th style={{
                  padding: "15px 12px",
                  fontWeight: "600",
                  fontSize: "13px",
                  borderRight: "1px solid rgba(255,255,255,0.2)",
                  minWidth: "140px"
                }}>
                  Remaining TL Seed (kg)
                  <br />
                  <input
                    type="text"
                    placeholder="Search"
                    className="form-control form-control-sm mt-2"
                    value={remainingTLSeed}
                    onChange={(e) => { setRemainingTLSeed(e.target.value); setPage(1); }}
                    style={{
                      backgroundColor: "#ffffff",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      fontSize: "12px",
                      color: "#333",
                      padding: "4px 8px"
                    }}
                  />
                </th>
                <th style={{
                  padding: "15px 12px",
                  fontWeight: "600",
                  fontSize: "13px",
                  borderRight: "1px solid rgba(255,255,255,0.2)",
                  minWidth: "100px"
                }}>
                  Remarks
                  <br />
                  <input
                    type="text"
                    placeholder="Search"
                    className="form-control form-control-sm mt-2"
                    value={remarks}
                    onChange={(e) => { setRemarks(e.target.value); setPage(1); }}
                    style={{
                      backgroundColor: "#ffffff",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      fontSize: "12px",
                      color: "#333",
                      padding: "4px 8px"
                    }}
                  />
                </th>
                <th style={{
                  padding: "15px 12px",
                  fontWeight: "600",
                  fontSize: "13px",
                  textAlign: "center",
                  minWidth: "100px"
                }}>
                  Action
                </th>
              </tr>
            </thead>
            <tbody style={{ fontSize: "13px" }}>
              {rows.map((data, index) => (
                <tr key={index} style={{
                  backgroundColor: index % 2 === 0 ? "#f8f9fa" : "#ffffff",
                  borderBottom: "1px solid #dee2e6"
                }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#e3f2fd";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = index % 2 === 0 ? "#f8f9fa" : "#ffffff";
                  }}>
                  <td style={{
                    padding: "12px 8px",
                    color: "#495057",
                    borderRight: "1px solid #dee2e6",
                    fontWeight: "500"
                  }}>
                    {data.producingFarmer}
                  </td>
                  <td style={{
                    padding: "12px 8px",
                    color: "#495057",
                    borderRight: "1px solid #dee2e6"
                  }}>
                    {data.pgCode}
                  </td>
                  <td style={{
                    padding: "12px 8px",
                    color: "#495057",
                    borderRight: "1px solid #dee2e6"
                  }}>
                    {data.caste}
                  </td>
                  <td style={{
                    padding: "12px 8px",
                    color: "#495057",
                    borderRight: "1px solid #dee2e6"
                  }}>
                    <span style={{
                      backgroundColor: data.seasonName === 'Rabi' ? '#d4edda' : '#fff3cd',
                      color: data.seasonName === 'Rabi' ? '#155724' : '#856404',
                      padding: "3px 8px",
                      borderRadius: "4px",
                      fontSize: "11px",
                      fontWeight: "500"
                    }}>
                      {data.seasonName}
                    </span>
                  </td>
                  <td style={{
                    padding: "12px 8px",
                    color: "#495057",
                    borderRight: "1px solid #dee2e6"
                  }}>
                    {data.seedName}
                  </td>
                  <td style={{
                    padding: "12px 8px",
                    color: "#495057",
                    borderRight: "1px solid #dee2e6"
                  }}>
                    {data.varietyName}
                  </td>
                  <td style={{
                    padding: "12px 8px",
                    textAlign: "center",
                    fontWeight: "600",
                    color: "#495057",
                    borderRight: "1px solid #dee2e6"
                  }}>
                    <span style={{
                      backgroundColor: '#007bff',
                      color: 'white',
                      padding: "3px 8px",                      borderRadius: "4px",
                      fontSize: "12px",
                      fontWeight: "600"
                    }}>
                      {data.totalTLSeed}
                    </span>
                  </td>
                  <td style={{
                    padding: "12px 8px",
                    color: "#495057",
                    borderRight: "1px solid #dee2e6",
                    fontWeight: "500"
                  }}>
                    {data.receivingFarmer}
                  </td>
                  <td style={{
                    padding: "12px 8px",
                    textAlign: "center",
                    fontWeight: "600",
                    color: "#495057",
                    borderRight: "1px solid #dee2e6"
                  }}>
                    <span style={{
                      backgroundColor: '#28a745',
                      color: 'white',
                      padding: "3px 8px",
                      borderRadius: "4px",
                      fontSize: "12px",
                      fontWeight: "600"
                    }}>
                      {data.receivingQuantity}
                    </span>
                  </td>
                  <td style={{
                    padding: "12px 8px",
                    color: "#495057",
                    borderRight: "1px solid #dee2e6"
                  }}>
                    {data.address}
                  </td>
                  <td style={{
                    padding: "12px 8px",
                    textAlign: "center",
                    fontWeight: "600",
                    color: "#495057",
                    borderRight: "1px solid #dee2e6"
                  }}>
                    <span style={{
                      backgroundColor: '#17a2b8',
                      color: 'white',
                      padding: "3px 8px",
                      borderRadius: "4px",
                      fontSize: "12px",
                      fontWeight: "600"
                    }}>
                      {data.areaCoverage}
                    </span>
                  </td>
                  <td style={{
                    padding: "12px 8px",
                    textAlign: "center",
                    fontWeight: "600",
                    color: "#495057",
                    borderRight: "1px solid #dee2e6"
                  }}>
                    <span style={{
                      backgroundColor: '#ffc107',
                      color: '#212529',
                      padding: "3px 8px",
                      borderRadius: "4px",
                      fontSize: "12px",
                      fontWeight: "600"
                    }}>
                      {data.remainingTLSeed}
                    </span>
                  </td>
                  <td style={{
                    padding: "12px 8px",
                    color: "#495057",
                    borderRight: "1px solid #dee2e6"
                  }}>
                    {data.remarks}
                  </td>
                  <td style={{
                    padding: "12px 8px",
                    textAlign: "center"
                  }}>
                    <div style={{ display: "flex", gap: "5px", justifyContent: "center" }}>
                      <button
                        className="btn btn-sm btn-success"
                        onClick={() => handleEdit(data.id)}
                        title="Edit"
                        style={{
                          padding: "4px 8px",
                          fontSize: "12px"
                        }}
                      >
                        <FaRegEdit />
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDelete(data.id)}
                        title="Delete"
                        style={{
                          padding: "4px 8px",
                          fontSize: "12px"
                        }}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className='d-flex justify-content-between pb-0'>
          <div className='p-2'>
            Showing {startItem} to {endItem} of {totalItems} items
          </div>
          <div className='p-2'>
            <Pagination className="mb-0">
              <Pagination.First onClick={() => handlePageChange(1)} disabled={page === 1} />
              <Pagination.Prev onClick={() => handlePageChange(page - 1)} disabled={page === 1} />
              {renderPaginationItems()}
              <Pagination.Next onClick={() => handlePageChange(page + 1)} disabled={page === totalpage} />
              <Pagination.Last onClick={() => handlePageChange(totalpage)} disabled={page === totalpage} />
            </Pagination>
          </div>
        </div>

        {/* Edit Dialog */}
        <Dialog open={openEdit} onClose={() => setOpenEdit(false)} maxWidth="md" fullWidth>
          <DialogTitle>Edit TL Outreach</DialogTitle>
          <DialogContent>
            <Typography variant="h6" gutterBottom>
              Remaining TL Seed: {selectedRow?.remainingTLSeed ?? "N/A"}
            </Typography>
            <Autocomplete
              options={farmers}
              getOptionLabel={(option) => option.farmerName || ""}
              value={
                farmers.find((farmer) => farmer.farmerId === selectedRow?.receivingFarmerId) || null
              }
              onChange={(event, newValue) => {
                const farmerId = newValue ? newValue.farmerId : "";
                const farmerName = newValue ? newValue.farmerName : "";
                handleFieldChange({
                  target: { name: "receivingFarmerId", value: farmerId },
                });
                handleFieldChange({
                  target: { name: "receivingFarmer", value: "" },
                });
                setSelectedRow((prev) => ({
                  ...prev,
                  receivingFarmerId: farmerId,
                  receivingFarmer: "",
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="PG Receiving Farmer"
                  fullWidth
                  margin="normal"
                />
              )}
              disabled={!!selectedRow?.receivingFarmer}
              clearOnEscape
              disableClearable={false}
              loading={loading}
            />

            <TextField
              label="Receiving Farmer"
              name="receivingFarmer"
              value={selectedRow?.receivingFarmer || ""}
              onChange={handleFieldChange}
              fullWidth
              margin="normal"
              disabled={!!selectedRow?.receivingFarmerId}
            />
            <TextField
              label="Address"
              name="address"
              value={selectedRow?.address || ""}
              onChange={handleFieldChange}
              fullWidth
              margin="normal"
              disabled={!!selectedRow?.receivingFarmerId}
            />
            <TextField
              label="Area Coverage"
              name="areaCoverage"
              value={selectedRow?.areaCoverage || ""}
              onChange={handleFieldChange}
              fullWidth
              margin="normal"
            />
            <TextField
              label="Receiving Quantity"
              name="receivingQuantity"
              value={selectedRow?.receivingQuantity || ""}
              onChange={(event) => {
                const inputValue = event.target.value;
                const isValidInput = /^(\d+(\.\d{0,3})?)?$/.test(inputValue);

                if (!isValidInput) return;

                const numericValue = parseFloat(inputValue) || 0;
                const currentRemainingTLSeed = parseFloat(selectedRow?.remainingTLSeed) || 0;
                const currentReceivingQuantity = parseFloat(selectedRow?.receivingQuantity) || 0;
                const total = currentRemainingTLSeed + currentReceivingQuantity;

                if (inputValue === "") {
                  setSelectedRow((prev) => ({
                    ...prev,
                    receivingQuantity: "",
                    remainingTLSeed: total.toFixed(3),
                  }));
                  handleFieldChange(event);
                  return;
                }

                if (!isNaN(numericValue) && numericValue > total) {
                  alert(`Receiving Quantity cannot be greater than total (${total.toFixed(3)})`);
                  return;
                }

                const newRemainingTLSeed = (total - numericValue).toFixed(3);

                setSelectedRow((prev) => ({
                  ...prev,
                  receivingQuantity: inputValue,
                  remainingTLSeed: newRemainingTLSeed,
                }));

                handleFieldChange(event);
              }}
              type="number"
              fullWidth
              margin="normal"
            />

            <ToggleButtonGroup
              value={unit}
              exclusive
              onChange={handleUnitChange}
              aria-label="unit selection"
            >
              <ToggleButton value={2} aria-label="qtl">
                QTL
              </ToggleButton>
              <ToggleButton value={1} aria-label="kg">
                KG
              </ToggleButton>
            </ToggleButtonGroup>

            <TextField
              label="Remarks"
              name="remarks"
              value={selectedRow?.remarks || ""}
              onChange={handleFieldChange}
              fullWidth
              margin="normal"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenEdit(false)} color="secondary">
              Cancel
            </Button>
            <Button onClick={handleSaveChanges} color="primary">
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={openDelete} onClose={() => setOpenDelete(false)}>
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to delete this TL-Outreach record?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDelete(false)} color="primary">Cancel</Button>
            <Button onClick={handleDeleteRow} color="primary">Delete</Button>
          </DialogActions>
        </Dialog>
      </div>
    </Box>
  );
};

export default Tlseed;

